import { UserAddOutlined } from '@ant-design/icons'
import { Button, Empty, message, Select, Tag } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { blue } from '../../../color'
import { AvatarHasName } from '../../../components/Common/Avatar'
import SelectUserDepartmentBox from '../../../components/SelectPeoplePopup/SelectUserDepartmentBox'
import { ASSIGNEE_TYPE } from '../../../constants'
import { handleFilterSelect } from '../../../utils/filterSelect'
import { ModalBody, ModalWrapper } from './ShareDocumentModalStyled'
import shareDocumentStore from './shareDocumentStore'

const SHARE_ASSIGNEE_TYPE = 'SHARE_DOCUMENT'
const defaultKeys = Array.from(Array(100).keys())

const ShareDocumentModal = props => {
  const {
    visible,
    onClose,
    currentDocument,
    userStore,
    selectPeopleStore,
    authenticationStore,
    companyStore,
  } = props

  const { mentionUserList } = userStore
  const { companyList } = companyStore
  const { isLoading } = shareDocumentStore
  const originList = selectPeopleStore.originDepartmentWithUserList || []

  const [defaultActiveKey, setDefaultActiveKey] = useState(defaultKeys)

  // ─── Khởi tạo key SHARE_DOCUMENT trong store ────────────────────────────────
  useEffect(() => {
    if (!selectPeopleStore.selectUserData[SHARE_ASSIGNEE_TYPE]) {
      selectPeopleStore.setSelectUserData({
        ...selectPeopleStore.selectUserData,
        [SHARE_ASSIGNEE_TYPE]: [],
      })
    }
    if (!selectPeopleStore.selectDepartmentData[SHARE_ASSIGNEE_TYPE]) {
      selectPeopleStore.setSelectDepartmentData({
        ...selectPeopleStore.selectDepartmentData,
        [SHARE_ASSIGNEE_TYPE]: [],
      })
    }
  }, [])

  // ─── Set assigneePopupType + companyCode khi modal mở ───────────────────────
  useEffect(() => {
    if (!visible) return
    selectPeopleStore.assigneePopupType = SHARE_ASSIGNEE_TYPE
    if (authenticationStore?.currentUser) {
      selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
    }
  }, [visible])

  // ─── Load danh sách phòng ban + người dùng khi companyCode thay đổi ─────────
  useEffect(() => {
    if (!visible || !selectPeopleStore.companyCode) return
    selectPeopleStore.getDepartmentWithUsers().catch(err => {
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    })
  }, [selectPeopleStore.companyCode, visible])

  // ─── Sync sharedWith vào store khi modal mở ──────────────────────────────────
  // Tự động detect department nếu toàn bộ user trong phòng ban đều có trong sharedWith
  useEffect(() => {
    if (!visible) return

    const sharedUsernames = Array.isArray(currentDocument?.sharedWith)
      ? currentDocument.sharedWith
      : []

    const restoredDepartments = originList
      .filter(
        dept =>
          dept.users?.length > 0 &&
          dept.users.every(u => sharedUsernames.includes(u.user_name))
      )
      .map(dept => ({
        assignee_type: ASSIGNEE_TYPE.DEPARTMENT,
        full_name: dept.name,
        id: dept.code,
        permission: SHARE_ASSIGNEE_TYPE,
      }))

    const usernamesInRestoredDepts = restoredDepartments.flatMap(
      dept =>
        originList
          .find(d => d.code === dept.id)
          ?.users?.map(u => u.user_name) || []
    )

    const restoredUsers = sharedUsernames
      .filter(username => !usernamesInRestoredDepts.includes(username))
      .map(username => {
        const found = mentionUserList?.find(u => u.username === username)
        return {
          assignee_type: ASSIGNEE_TYPE.USER,
          full_name: found ? found.display : username,
          id: username,
          permission: SHARE_ASSIGNEE_TYPE,
          image_id: found?.image,
        }
      })

    selectPeopleStore.setSelectUserData({
      ...selectPeopleStore.selectUserData,
      [SHARE_ASSIGNEE_TYPE]: restoredUsers,
    })
    selectPeopleStore.setSelectDepartmentData({
      ...selectPeopleStore.selectDepartmentData,
      [SHARE_ASSIGNEE_TYPE]: restoredDepartments,
    })
  }, [
    visible,
    currentDocument?.sharedWith,
    selectPeopleStore.originDepartmentWithUserList,
  ])

  // ─── Khi tick lại department → xóa user riêng lẻ trùng khỏi selectUserData ──
  useEffect(() => {
    const currentDepts =
      selectPeopleStore.selectDepartmentData[SHARE_ASSIGNEE_TYPE] || []
    if (currentDepts.length === 0) return

    const usernamesInDepts = currentDepts.flatMap(
      dept =>
        originList
          .find(d => d.code === dept.id)
          ?.users?.map(u => u.user_name) || []
    )

    const currentUsers =
      selectPeopleStore.selectUserData[SHARE_ASSIGNEE_TYPE] || []
    if (!currentUsers.some(u => usernamesInDepts.includes(u.id))) return

    selectPeopleStore.setSelectUserData({
      ...selectPeopleStore.selectUserData,
      [SHARE_ASSIGNEE_TYPE]: currentUsers.filter(
        u => !usernamesInDepts.includes(u.id)
      ),
    })
  }, [selectPeopleStore.selectDepartmentData[SHARE_ASSIGNEE_TYPE]])

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const closeAndResetForm = () => {
    shareDocumentStore.clearStore()
    selectPeopleStore.setSelectUserData({
      ...selectPeopleStore.selectUserData,
      [SHARE_ASSIGNEE_TYPE]: [],
    })
    selectPeopleStore.setSelectDepartmentData({
      ...selectPeopleStore.selectDepartmentData,
      [SHARE_ASSIGNEE_TYPE]: [],
    })
    onClose()
  }

  const assign = () => {
    const selectedUsers =
      selectPeopleStore.selectUserData[SHARE_ASSIGNEE_TYPE] || []
    const selectedDepartments =
      selectPeopleStore.selectDepartmentData[SHARE_ASSIGNEE_TYPE] || []

    const deptUsernames = selectedDepartments.flatMap(
      dept =>
        originList
          .find(d => d.code === dept.id)
          ?.users?.map(u => u.user_name) || []
    )

    const sharedWith = Array.from(
      new Set([...selectedUsers.map(u => u.id), ...deptUsernames])
    )

    shareDocumentStore
      .share({ id: currentDocument.id, sharedWith })
      .then(() => {
        closeAndResetForm()
        message.success('Cập nhật quyền chia sẻ thành công')
      })
      .catch(e => {
        shareDocumentStore.isLoading = false
        message.error(
          e?.errorCode === 'PERMISSION-100'
            ? 'Không có quyền chia sẻ'
            : 'Đã có lỗi xảy ra!'
        )
      })
  }

  // Xóa user khỏi danh sách đã chọn
  // Nếu user thuộc department → explode department thành user riêng lẻ rồi bỏ user đó
  const handleRemoveSelected = item => {
    if (item._deptId) {
      const dept = originList.find(d => d.code === item._deptId)
      const explodedUsers = dept
        ? dept.users
            .filter(u => u.user_name !== item.id)
            .map(u => ({
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: u.name_uppercase,
              id: u.user_name,
              permission: SHARE_ASSIGNEE_TYPE,
              image_id: u.image_id,
            }))
        : []
      selectPeopleStore.setSelectDepartmentData({
        ...selectPeopleStore.selectDepartmentData,
        [SHARE_ASSIGNEE_TYPE]: (
          selectPeopleStore.selectDepartmentData[SHARE_ASSIGNEE_TYPE] || []
        ).filter(el => el.id !== item._deptId),
      })
      selectPeopleStore.setSelectUserData({
        ...selectPeopleStore.selectUserData,
        [SHARE_ASSIGNEE_TYPE]: [
          ...(selectPeopleStore.selectUserData[SHARE_ASSIGNEE_TYPE] || []),
          ...explodedUsers,
        ],
      })
    } else {
      selectPeopleStore.setSelectUserData({
        ...selectPeopleStore.selectUserData,
        [SHARE_ASSIGNEE_TYPE]: (
          selectPeopleStore.selectUserData[SHARE_ASSIGNEE_TYPE] || []
        ).filter(el => el.id !== item.id),
      })
    }
  }

  // ─── Derived state ────────────────────────────────────────────────────────────
  const selectedDepartments =
    selectPeopleStore.selectDepartmentData[SHARE_ASSIGNEE_TYPE] || []
  const selectedUsers =
    selectPeopleStore.selectUserData[SHARE_ASSIGNEE_TYPE] || []

  // Expand department thành từng user để hiển thị ở cột phải
  const displayList = [
    ...selectedDepartments.flatMap(dept => {
      const found = originList.find(d => d.code === dept.id)
      if (!found) return []
      return found.users.map(u => ({
        assignee_type: ASSIGNEE_TYPE.USER,
        full_name: u.name_uppercase,
        id: u.user_name,
        image_id: u.image_id,
        _deptId: dept.id,
      }))
    }),
    ...selectedUsers,
  ]

  const selectedCount = selectedDepartments.length + selectedUsers.length

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <ModalWrapper
      width={900}
      footer={[
        <Button
          key="back"
          onClick={closeAndResetForm}
          style={{ borderRadius: 6 }}>
          Hủy bỏ
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={assign}
          style={{ borderRadius: 6, background: blue, borderColor: blue }}>
          Lưu thay đổi
        </Button>,
      ]}
      closable={true}
      visible={visible}
      onCancel={closeAndResetForm}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UserAddOutlined style={{ color: blue }} />
          <span>Chia sẻ: {currentDocument?.name}</span>
        </div>
      }>
      <ModalBody>
        <div style={{ display: 'flex', gap: 4 }}>
          {/* Cột trái: chọn công ty + chọn người */}
          <div style={{ flexGrow: 1 }}>
            <Select
              style={{ width: '100%', marginBottom: 8 }}
              onChange={value => {
                selectPeopleStore.companyCode = value
              }}
              value={selectPeopleStore.companyCode}
              placeholder="Chọn công ty"
              showSearch
              allowClear
              filterOption={handleFilterSelect}>
              {companyList.map(item => (
                <Select.Option key={item.code} value={item.code}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
            <SelectUserDepartmentBox
              defaultKeys={defaultKeys}
              defaultActiveKey={defaultActiveKey}
              setDefaultActiveKey={setDefaultActiveKey}
              isOpenCollapse={true}
              isVisibleSelectPeoplePopup={visible}
              disableSelectDepartment={false}
              onlyOneUser={false}
              onlySelectOne={false}
              userDisableList={[]}
              departmentDisableList={[]}
            />
          </div>

          {/* Cột phải: danh sách đã chọn */}
          <div
            style={{
              flex: '0 0 250px',
              maxWidth: 250,
              maxHeight: 'calc(100vh - 260px)',
              overflowY: 'auto',
              backgroundColor: '#fff',
              borderLeft: '1px solid #f0f0f0',
              padding: '0 10px 15px 10px',
            }}>
            <div
              style={{
                margin: '0 -10px 10px -10px',
                padding: 15,
                borderBottom: '1px solid #f0f0f0',
                fontWeight: 500,
                backgroundColor: '#e9f7ff',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}>
              Danh sách đã chọn:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {displayList.length > 0 ? (
                displayList.map(el => (
                  <Tag
                    color="blue"
                    key={el._deptId ? `${el._deptId}-${el.id}` : el.id}
                    closable
                    onClose={() => handleRemoveSelected(el)}
                    style={{ display: 'flex', alignItems: 'center' }}>
                    <AvatarHasName
                      imgId={el.image_id}
                      size={22}
                      name={el.full_name}
                    />
                  </Tag>
                ))
              ) : (
                <Empty
                  style={{ width: '100%' }}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Chưa có ai được chọn"
                />
              )}
            </div>
          </div>
        </div>
      </ModalBody>
    </ModalWrapper>
  )
}

ShareDocumentModal.propTypes = {}

export default inject(
  'resourcesStore',
  'userStore',
  'selectPeopleStore',
  'authenticationStore',
  'companyStore'
)(observer(ShareDocumentModal))
